import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import DocBrandBand, { ScopeSections } from '../../components/DocBrandBand';
import { useConfirm } from '../../components/ConfirmDialog';
import SignaturePad from '../../components/SignaturePad';
import { Card, SectionTitle, Field, Spinner, ErrorNote } from '../../components/ui';
import { money, fmtDateTime, composeAddress, normSection, proposalTotals, legacyTotal } from '../../lib/format';

export default function PublicProposal() {
  const { token } = useParams();
  const { data, error, loading, reload } = useAsync(() => publicApi.get('/p/' + token), [token]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [ask, confirmDialog] = useConfirm();
  const padRef = useRef();
  const [name, setName] = useState('');
  const [padEmpty, setPadEmpty] = useState(true);

  if (loading) return <Spinner label="Loading your proposal" />;
  if (error) {
    return (
      <div className="view">
        <ErrorNote onRetry={reload}>
          We couldn’t open this proposal. The link may have expired — please contact us and we’ll send a fresh one.
        </ErrorNote>
      </div>
    );
  }

  const { company: c, proposal: p, customer: cu, agreement: a } = data;
  const signed = a?.status === 'signed';
  const sections = (p.sections || []).map(normSection);
  const t = proposalTotals(sections, p.taxPct, legacyTotal(p));
  const declined = p.status === 'rejected';

  // Signing accepts the proposal and its terms in one step.
  async function sign() {
    if (!name.trim()) return setActionError('Please type your full name.');
    if (padRef.current?.isEmpty()) return setActionError('Please draw your signature.');
    setBusy(true); setActionError('');
    try {
      await publicApi.post(`/p/${token}/sign`, { signerName: name.trim(), signatureDataUrl: padRef.current.toDataURL() });
      reload();
    } catch (e) { setActionError(e.message); }
    finally { setBusy(false); }
  }

  const respond = async (action) => {
    if (action === 'reject' && !await ask({ title: 'Decline this quote?', body: 'We’ll let the team know you’re not moving forward.', confirmLabel: 'Decline quote' })) return;
    setBusy(true);
    setActionError('');
    try { await publicApi.post(`/p/${token}/${action}`); reload(); }
    catch (e) { setActionError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <article className="doc">
      <DocBrandBand label="Proposal" company={c} meta={[
        { label: 'Proposal No.', value: p.number },
        { label: 'Date', value: p.date },
      ]} />

      <div className="view">
        <Card tight>
          <div className="row between" style={{ alignItems: 'flex-start', gap: 'var(--s-4)' }}>
            <div>
              <div className="sb">Proposal to</div>
              <div className="nm">{cu?.name}</div>
              <div className="sb">{composeAddress(cu)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="sb">Job location</div>
              <div className="nm">{p.jobLocation || composeAddress(cu)}</div>
            </div>
          </div>
        </Card>

        <Card tight>
          <p className="nm">{c.companyName} will provide the following services:</p>
          <ScopeSections sections={sections} />
        </Card>

        {c.bannerNote ? <div className="note warn">{c.bannerNote}</div> : null}

        {p.opNotesText ? (
          <Card tight>
            <SectionTitle>Standard operating procedures</SectionTitle>
            <p className="doc-prose">{p.opNotesText}</p>
          </Card>
        ) : null}

        {p.warrantyText ? (
          <Card tight>
            <SectionTitle>Warranty</SectionTitle>
            <p className="doc-prose">{p.warrantyText}</p>
          </Card>
        ) : null}

        <Card tight>
          {t.hasBreakdown ? (
            <>
              <div className="doc-line"><span className="d sb">Subtotal</span><span className="a">{money(t.subtotal)}</span></div>
              <div className="doc-line"><span className="d sb">Tax ({t.taxPct}%)</span><span className="a">{money(t.tax)}</span></div>
            </>
          ) : null}
          <div className="doc-total"><span className="nm">Total</span><span className="v">{money(t.total)}</span></div>
        </Card>

        {(p.photos || []).length ? (
          <Card tight>
            <SectionTitle>Photos</SectionTitle>
            <div className="photo-gallery" style={{ marginTop: 'var(--s-2)' }}>
              {p.photos.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                  <img src={src} alt={`Property photo ${i + 1}`} loading="lazy" />
                </a>
              ))}
            </div>
          </Card>
        ) : null}

        {p.pricingNotesText ? (
          <Card tight><SectionTitle>Pricing notes</SectionTitle><p className="doc-prose">{p.pricingNotesText}</p></Card>
        ) : null}
        {p.notesText ? (
          <Card tight><SectionTitle>Notes</SectionTitle><p className="doc-prose">{p.notesText}</p></Card>
        ) : null}

        {a?.termsText ? (
          <Card tight><SectionTitle>Terms &amp; conditions</SectionTitle><p className="doc-prose">{a.termsText}</p></Card>
        ) : null}
        {a?.paymentScheduleText ? (
          <Card tight><SectionTitle>Payment schedule</SectionTitle><p className="doc-prose">{a.paymentScheduleText}</p></Card>
        ) : null}
        {a?.exclusions ? (
          <Card tight><SectionTitle>Exclusions</SectionTitle><p className="doc-prose">{a.exclusions}</p></Card>
        ) : null}

        {signed ? (
          <>
            <div className="note ok"><b>✓ Signed</b> by {a.signature?.name} on {fmtDateTime(a.signedAt)}. Thank you!</div>
            {a.signature?.dataUrl ? (
              <Card tight>
                <div className="sb">Signature</div>
                <img src={a.signature.dataUrl} alt={`Signature of ${a.signature.name}`} style={{ maxWidth: '100%' }} />
              </Card>
            ) : null}
          </>
        ) : declined ? null : (
          <Card>
            <SectionTitle sub="Draw your signature and type your name to accept this proposal and its terms.">
              Sign &amp; accept
            </SectionTitle>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <SignaturePad ref={padRef} onChange={setPadEmpty} />
            </div>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <Field label="Your full name" id="signer">
                <input id="signer" className="fi" value={name} onChange={e => setName(e.target.value)}
                  autoComplete="name" placeholder="Type your full name" />
              </Field>
            </div>
            <p className="sb" style={{ marginTop: 'var(--s-2)' }}>
              By signing you accept this proposal and agree to the terms above. Your signature, name, time and IP address are recorded.
            </p>
            <button className="btn grn block" style={{ marginTop: 'var(--s-3)' }}
              onClick={sign} disabled={busy || padEmpty || !name.trim()}>
              {busy ? 'Signing…' : '✓ Sign & accept proposal'}
            </button>
          </Card>
        )}
      </div>

      {actionError ? <div className="note err" role="alert">{actionError}</div> : null}
      {confirmDialog}

      <div className="actions">
        {declined ? (
          <div className="note err" style={{ flex: 1, textAlign: 'center' }}>✕ Quote declined</div>
        ) : !signed ? (
          <button className="btn dng" disabled={busy} onClick={() => respond('reject')}>Decline</button>
        ) : null}
        {/* A real PDF rather than the browser's print-to-PDF: this is the copy
            the customer keeps, and it matches the one attached to their email. */}
        <a className="btn no-print" href={`/api/p/${token}/pdf?download=1`}>📄 Download PDF</a>
        <button className="btn no-print" onClick={() => window.print()}>🖨 Print</button>
      </div>
    </article>
  );
}
